import * as d3 from 'd3';
import d3Tip from 'd3-tip';
import { DURATION, colors, closest } from '../utils';
import { sliderHorizontal } from 'd3-simple-slider';
import './d3-cashflowcoveragechart.css';

const DELAY_MULTIPLE = 100;

export default function () {
  const margin = { top: 10, right: 200, bottom: 80, left: 60 };

  let width = 400;
  let height = 300;
  let svg;

  let selected;
  let barData;
  let selectedYearAssetsRunOut;
  let assetXSReturns;
  let fundingLevel;
  let oldSelectedYears;
  let chartWidth;
  let chartHeight;
  let label;
  let t;
  let delay;
  let tip;
  let markers;
  let x;
  let xLinear;
  let y;
  let slider;
  let assetXSReturnKeyFigure;
  let fundingLevelKeyFigure;

  const dispatch = d3.dispatch('selected');

  function chart(selection, isResize) {
    selection.each(function (data) {
      const dataChanged = JSON.stringify(barData) !== JSON.stringify(data.benefitPayments);

      barData = data.benefitPayments;
      selectedYearAssetsRunOut = data.selectedYearAssetsRunOut;
      assetXSReturns = data.assetXSReturns;
      fundingLevel = data.fundingLevel;

      const marginRight = width <= 800 ? 100 : 200;

      chartWidth = Math.max(width - margin.left - marginRight, 0);
      chartHeight = Math.max(height - margin.top - margin.bottom, 0);

      t = d3.transition().duration(isResize ? 0 : DURATION);
      delay = isResize || dataChanged ? 0 : DELAY_MULTIPLE;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        g.append('g')
          .attr('class', 'cashflow-coverage-chart__slider');

        createAxes();
        createLabel();

        g.append('g')
          .attr('class', 'cashflow-coverage-chart__bars');

        //createTooltip(); // TODO: Investigate why tooltip is not rendered correctly
        createMarkers();
        assetXSReturnKeyFigure = createKeyFigure();
        fundingLevelKeyFigure = createKeyFigure();
        createSlider();
      }

      svg.attr('width', width)
        .attr('height', height);

      drawAxes();
      drawLabel();
      drawBars();
      drawMarkers();
      drawKeyFigure(assetXSReturnKeyFigure, [chartWidth + 20, width <= 800 ? 50 : 100], 'Asset XS Return', d3.format('.2%')(selected));
      drawKeyFigure(fundingLevelKeyFigure, [chartWidth + 20, 0], 'Funding Level', d3.format('.1%')(fundingLevel));
      drawSlider();

      oldSelectedYears = selectedYearAssetsRunOut;
    });
  }

  function createScales() {
    x = d3.scaleBand()
      .padding(0.15)
      .range([0, chartWidth]);

    const n = barData.length;
    const step = chartWidth / (n - 0.45);

    xLinear = d3.scaleLinear()
      .range([step * 0.65, chartWidth - step * 0.65]);

    y = d3.scaleLinear()
      .range([chartHeight, 0]);

    var yMax = d3.max(barData.map(function (d) { return d.Amount; }));

    x.domain(barData.map(function (d) { return d.Year; }).sort());
    xLinear.domain(d3.extent(barData.map(function (d) { return d.Year; })));
    y.domain([0, yMax]);
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'cashflow-coverage-chart__axis cashflow-coverage-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'cashflow-coverage-chart__axis cashflow-coverage-chart__axis--y');

    const sliderAxis = svg.select('.cashflow-coverage-chart__slider')
      .append('g')
      .attr('class', 'cashflow-coverage-chart__axis cashflow-coverage-chart__axis--slider');

    sliderAxis.append('line')
      .attr('class', 'baseline-halo');

    sliderAxis.append('line')
      .attr('class', 'baseline');
  }

  function createLabel() {
    svg.append('text')
      .attr('class', 'cashflow-coverage-chart__xlabel')
      .attr('dy', '0.8em')
      .attr('text-anchor', 'middle')
      .text('Year');

    svg.append('text')
      .attr('class', 'cashflow-coverage-chart__ylabel')
      .attr('dy', '-0.8em')
      .attr('dx', '-1.7em')
      .attr('transform', 'rotate(-90)');
  }

  function createTooltip() {
    tip = d3Tip()
      .attr('class', 'd3-tip')
      .offset([-10, 0])
      .html(function (d) {
        return "<span>" + d3.format(',r')(d.Amount) + "</span>";
      });

    svg.call(tip);
  }

  function createMarkers() {
    markers = [
      {
        line: svg.select('g')
          .append('line')
          .attr('stroke', 'black')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2'),
        invisibleLine: svg.select('g')
          .append('line')
          .attr('stroke', 'transparent')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2'),
        tip: d3Tip()
          .attr('class', 'd3-tip animate')
          .direction('w')
          .offset([-50, -10])
          .html(function (d) {
            return "<span>Risk: " + d + "</span>";
          }),
      },
      {
        line: svg.select('g')
          .append('line')
          .attr('stroke', 'black')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2'),
        invisibleLine: svg.select('g')
          .append('line')
          .attr('stroke', 'transparent')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2'),
        tip: d3Tip()
          .attr('class', 'd3-tip animate')
          .direction('w')
          .offset([0, -10])
          .html(function (d) {
            return "<span>Expected: " + d + "</span>";
          }),
      }
    ];

    markers.forEach(function (d) { svg.call(d.tip); });
  }

  function createKeyFigure() {
    const keyFigure = svg.select('g')
      .append('g')
      .attr('class', 'cashflow-coverage-chart__key-figure');

    keyFigure.append('rect')
      .attr('class', 'cashflow-coverage-chart__key-figure--rect')
      .attr('x', 15)
      .attr('y', 26)
      .attr('width', width <= 800 ? 75 : 150)
      .attr('height', 56)
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('fill', '#bad80a');

    const text = keyFigure.append('text');

    text.append('tspan')
      .attr('class', 'cashflow-coverage-chart__key-figure--label')
      .attr('text-anchor', 'middle')
      .attr('font-size', 14)
      .attr('fill', '#000');

    text.append('tspan')
      .attr('class', 'cashflow-coverage-chart__key-figure--value')
      .attr('text-anchor', 'middle')
      .attr('font-weight', 700)
      .attr('font-size', 42)
      .attr('fill', '#000');

    return keyFigure;
  }

  function createSlider() {
    slider = sliderHorizontal()
      .domain(d3.extent(assetXSReturns))
      .width(width <= 800 ? 50 : 140)
      .tickFormat(d3.format('.1%'))
      .ticks(5)
      .on('end', function dragged(d) {
        const { curr } = closest(d, assetXSReturns);
        dispatch.call('selected', chart, 'assetXSReturn', curr);
      });

    const sliderContainer = svg.select('.cashflow-coverage-chart__slider');
    sliderContainer.call(slider);
  }

  function drawAxes() {
    svg.select('.cashflow-coverage-chart__axis--x')
      .call(d3.axisBottom(xLinear).tickFormat(d3.format('4d')).ticks(width <= 800 ? 5 : 20))
      .attr('transform', 'translate(0,' + chartHeight + ')')
      .selectAll('.tick text')
      .attr('dx', width <= 800 ? -7 : -14)
      .attr('dy', width <= 800 ? 7 : 14)
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    const format = d3.max(barData, function (d) { return d.Amount; }) < 10 ? d3.format('.2f') : d3.format('.2s');

    const yAxis = svg.select('.cashflow-coverage-chart__axis--y')
      .call(d3.axisLeft(y).tickFormat(format).tickSize(-chartWidth));

    yAxis.select('.domain').remove();

    yAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    yAxis.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);

    const sliderAxis = svg.select('.cashflow-coverage-chart__axis--slider');
    sliderAxis.attr('transform', 'translate(20,' + 200 + ')');
  }

  function drawLabel() {
    svg.select('.cashflow-coverage-chart__xlabel')
      .attr('x', width / 2)
      .attr('y', chartHeight + margin.top + margin.bottom - 20);

    svg.select('.cashflow-coverage-chart__ylabel')
      .attr('x', -height / 2)
      .attr('y', margin.left / 2)
      .text(label + ' £ p.a.');
  }

  function drawBars() {
    const barsFull = svg.select('.cashflow-coverage-chart__bars')
      .selectAll('.cashflow-coverage-chart__bar--full')
      .data(barData, function (d) { return d.Year; });

    barsFull.exit()
      .remove();

    barsFull.enter()
      .append('rect')
      .attr('class', 'cashflow-coverage-chart__bar--full')
      //.on('mouseover', tip.show)
      //.on('mouseout', tip.hide)
      .merge(barsFull)
      .attr('x', function (d) { return x(d.Year); })
      .attr('width', x.bandwidth())
      .attr('fill', '#ece7f2')
      .transition(t)
      .attr('y', function (d) { return y(d.Amount); })
      .attr('height', function (d) { return chartHeight - y(d.Amount); });

    const barsRisk = svg.select('.cashflow-coverage-chart__bars')
      .selectAll('.cashflow-coverage-chart__bar--risk')
      .data(barData.filter(function (d) { return d.Year <= selectedYearAssetsRunOut.Expected; }), function (d) { return d.Year; });

    barsRisk.exit()
      .transition(t)
      .delay(function (d) { return oldSelectedYears === undefined ? 0 : delay * Math.abs(d.Year - oldSelectedYears.Expected); })
      .attr('y', function (d) { return y(0); })
      .attr('height', 0)
      .remove();

    barsRisk.enter()
      .append('rect')
      .attr('class', 'cashflow-coverage-chart__bar--risk')
      .attr('height', 0)
      .attr('y', function (d) { return y(0); })
      .merge(barsRisk)
      .attr('x', function (d) { return x(d.Year); })
      .attr('width', x.bandwidth())
      .attr('fill', '#a6bddb')
      .transition(t)
      .delay(function (d) { return oldSelectedYears === undefined ? 0 : delay * Math.abs(d.Year - oldSelectedYears.Expected); })
      .attr('y', function (d) { return y(d.Amount); })
      .attr('height', function (d) { return chartHeight - y(d.Amount); });

    const barsExpected = svg.select('.cashflow-coverage-chart__bars')
      .selectAll('.cashflow-coverage-chart__bar--expected')
      .data(barData.filter(function (d) { return d.Year <= selectedYearAssetsRunOut.Risk; }), function (d) { return d.Year; });

    barsExpected.exit()
      .transition(t)
      .delay(function (d) { return oldSelectedYears === undefined ? 0 : delay * Math.abs(d.Year - oldSelectedYears.Risk); })
      .attr('y', function (d) { return y(0); })
      .attr('height', 0)
      .remove();

    barsExpected.enter()
      .append('rect')
      .attr('class', 'cashflow-coverage-chart__bar--expected')
      .attr('height', 0)
      .attr('y', function (d) { return y(0); })
      .merge(barsExpected)
      .attr('x', function (d) { return x(d.Year); })
      .attr('width', x.bandwidth())
      .attr('fill', '#2b8cbe')
      .transition(t)
      .delay(function (d) { return oldSelectedYears === undefined ? 0 : delay * Math.abs(d.Year - oldSelectedYears.Risk); })
      .attr('y', function (d) { return y(d.Amount); })
      .attr('height', function (d) { return chartHeight - y(d.Amount); });
  }

  function drawMarkers() {
    markers[0].line
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .transition()
      .duration(DURATION)
      .ease(d3.easeQuadInOut)
      .attr('x1', xLinear(selectedYearAssetsRunOut.Risk))
      .attr('x2', xLinear(selectedYearAssetsRunOut.Risk));

    markers[0].invisibleLine
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('x1', xLinear(selectedYearAssetsRunOut.Risk))
      .attr('x2', xLinear(selectedYearAssetsRunOut.Risk));

    markers[1].line
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .transition()
      .duration(DURATION)
      .ease(d3.easeQuadInOut)
      .attr('x1', xLinear(selectedYearAssetsRunOut.Expected))
      .attr('x2', xLinear(selectedYearAssetsRunOut.Expected));

    markers[1].invisibleLine
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('x1', xLinear(selectedYearAssetsRunOut.Expected))
      .attr('x2', xLinear(selectedYearAssetsRunOut.Expected));

    markers.forEach(function (d, i) { d.tip.show(i === 0 ? selectedYearAssetsRunOut.Risk : selectedYearAssetsRunOut.Expected, d.invisibleLine.node()); });
  }

  function drawKeyFigure(keyFigure, pos, label, value) {
    keyFigure.attr('transform', 'translate(' + pos[0] + ',' + pos[1] + ')');

    keyFigure.select('.cashflow-coverage-chart__key-figure--rect')
      .attr('width', width <= 800 ? 60 : 150)
      .attr('height', width <= 800 ? 30 : 56);;

    keyFigure.select('.cashflow-coverage-chart__key-figure--label')
      .attr('font-size', width <= 800 ? 9 : 14)
      .attr('x', width <= 800 ? 45 : 90)
      .attr('y', 20)
      .text(label);

    keyFigure.select('.cashflow-coverage-chart__key-figure--value')
      .attr('font-size', width <= 800 ? 18 : 48)
      .attr('x', width <= 800 ? 45 : 90)
      .attr('y', width <= 800 ? 48 : 70)
      .text(value);
  }

  function drawSlider() {
    const sliderContainer = svg.select('.cashflow-coverage-chart__slider');
    sliderContainer.attr('transform', 'translate(' + (chartWidth + 40) + ',' + (width <= 800 ? 120 : 200) + ')');
    sliderContainer.call(slider);

    slider.value(selected);

    sliderContainer.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    sliderContainer.selectAll('text')
      .attr('dx', '-12px')
      .attr('dy', '12px')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.label = function (value) {
    if (!arguments.length) return label;
    label = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  chart.cleanup = function () {
    markers.forEach(function (d) { d.tip.hide(); }); // TODO: We should remove not just hide the tooltips
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-cashflowcoveragechart.js